// COLLEGE OVERVIEW → batch comparison, revealing readiness gaps across
// batches (Q3: "which batches have the largest readiness gaps?"). Every
// number here is a rollup of readinessEngine.getStudentReadiness() per
// student — never a separately-computed batch score. See
// server/services/batchAnalyticsService.js for the aggregation.
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from '../../services/api';
import { getUser } from '../../utils/authUtil';
import { scoreColor, readinessLevelColor, readinessLevelLabel } from '../../components/diagnostic/diagnosticHelpers';
import { SkeletonCard } from '../../components/ui/Skeleton';
import '../admin/Analytics.css';
import './BatchDrilldown.css';

const READINESS_LEVELS = ['top_performer', 'placement_ready', 'moderate', 'needs_improvement'];

// Same 3-tier scale the student side uses (scoreColor) — see BatchAnalytics.js
// for why this replaced a separately hand-copied 4-tier scale.
const sc = v => v == null ? '#94a3b8' : scoreColor(v);
const fmt = (v, s = '') => (v == null ? '—' : `${v}${s}`);

const BatchesOverview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = getUser();
  const isSuperAdmin = (user?.realRole || user?.role) === 'super_admin';
  // The backend already accepts ?college_id= for super_admin (whose own
  // college_id is null) — this page previously never read it from the URL
  // at all, so the mechanism was completely unreachable from the UI: a
  // super_admin visiting this page always got a raw "college_id is
  // required" 400 with no way to proceed, even when the URL had the param.
  const collegeIdParam = searchParams.get('college_id');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true); setError('');
      try {
        const res = await API.get('/reports/batches/overview', collegeIdParam ? { params: { college_id: collegeIdParam } } : undefined);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load batch overview');
      } finally { setLoading(false); }
    })();
  }, [collegeIdParam]);

  if (loading) {
    return (
      <div className="axh-page">
        <div className="axh-kpi-grid">
          {Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} lines={1} />)}
        </div>
        <SkeletonCard lines={5} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="axh-page">
        <div className="axh-error">
          {isSuperAdmin && !collegeIdParam
            ? 'Select a college to view its batch overview — open a college from Colleges → View Analytics.'
            : error}
        </div>
      </div>
    );
  }

  const cs = data?.college_summary;
  const batches = data?.batches || [];

  return (
    <div className="axh-page">
      <div className="axh-topbar">
        <div>
          <h1>Batch Readiness Overview</h1>
          <p>College → Batch drill-down. Batches below are sorted worst-first by placement-ready share, using the same readiness resolver every student sees on their own dashboard.</p>
        </div>
      </div>

      {batches.length === 0 ? (
        <div className="axh-empty">No batches exist for this college yet.</div>
      ) : (
        <>
          {cs && (
            <>
              <div className="axh-kpi-grid">
                <div className="axh-kpi"><div className="v">{cs.total_students}</div><div className="l">Total Students</div></div>
                <div className="axh-kpi"><div className="v">{cs.evaluated}</div><div className="l">Evaluated</div><div className="sub">{cs.insufficient_evidence} insufficient evidence</div></div>
                <div className="axh-kpi"><div className="v" style={{ color: sc(cs.placement_ready_pct) }}>{fmt(cs.placement_ready_pct, '%')}</div><div className="l">College Placement-Ready</div></div>
                <div className="axh-kpi"><div className="v" style={{ color: sc(cs.avg_readiness_score) }}>{fmt(cs.avg_readiness_score)}</div><div className="l">Avg Readiness Score</div></div>
                <div className="axh-kpi"><div className="v">{cs.total_batches}</div><div className="l">Batches</div></div>
              </div>

              {/* Readiness distribution — the first thing this page should
                  answer ("what is the current placement-readiness
                  situation?"), shown as an actual breakdown rather than a
                  single percentage. Every count comes straight from
                  college_summary.by_level (server-computed); insufficient-
                  evidence students are shown honestly as their own segment,
                  never folded into "needs improvement". */}
              {cs.evaluated > 0 && (
                <section className="axh-section">
                  <div className="axh-section-head"><h2>Readiness Distribution</h2></div>
                  <div className="axh-card">
                    <div className="axh-bars">
                      {READINESS_LEVELS.map(level => (
                        <div className="axh-bar-row" key={level}>
                          <span className="axh-bar-label">{readinessLevelLabel(level)}</span>
                          <div className="axh-bar-track">
                            <div className="axh-bar-fill" style={{ width: `${cs.total_students > 0 ? (cs.by_level[level] / cs.total_students) * 100 : 0}%`, background: readinessLevelColor(level) }} />
                          </div>
                          <span className="axh-bar-val">{cs.by_level[level]}</span>
                        </div>
                      ))}
                      {cs.insufficient_evidence > 0 && (
                        <div className="axh-bar-row">
                          <span className="axh-bar-label">Insufficient Data</span>
                          <div className="axh-bar-track">
                            <div className="axh-bar-fill" style={{ width: `${cs.total_students > 0 ? (cs.insufficient_evidence / cs.total_students) * 100 : 0}%`, background: '#94a3b8' }} />
                          </div>
                          <span className="axh-bar-val">{cs.insufficient_evidence}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              )}
            </>
          )}

          <section className="axh-section">
            <div className="axh-section-head"><h2>Batches</h2><p>Click a batch to drill into topic weaknesses, mistakes, trend, and students.</p></div>
            <div className="axh-card">
              <div className="axh-table-wrap">
                <table className="axh-table">
                  <thead>
                    <tr>
                      <th>Batch</th><th>Type</th><th>Students</th><th>Evaluated</th>
                      <th>Placement Ready</th><th>Needs Improvement</th><th>Avg Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map(b => (
                      <tr
                        key={b.batch_id}
                        role="button"
                        tabIndex={0}
                        aria-label={`View analytics for ${b.batch_name}`}
                        onClick={() => navigate(`/admin/batches/${b.batch_id}/analytics`)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/admin/batches/${b.batch_id}/analytics`); } }}
                      >
                        <td className="name-cell">{b.batch_name}</td>
                        <td><span className="axh-pill" style={{ background: '#f1f5f9', color: '#334155' }}>{b.batch_type}</span></td>
                        <td>{b.student_count}</td>
                        <td>{b.evaluated}{b.insufficient_evidence > 0 && <span style={{ color: '#94a3b8' }}> ({b.insufficient_evidence} insufficient)</span>}</td>
                        <td>{b.evaluated === 0 ? '—' : <span style={{ color: sc(b.placement_ready_pct), fontWeight: 700 }}>{fmt(b.placement_ready_pct, '%')}</span>}</td>
                        <td>{b.by_level?.needs_improvement ?? 0}</td>
                        <td>{b.evaluated === 0 ? '—' : <span style={{ color: sc(b.avg_readiness_score), fontWeight: 700 }}>{fmt(b.avg_readiness_score)}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default BatchesOverview;
